<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useId } from 'vue';
import { NButton, useThemeVars } from 'naive-ui';
import { OnBoarding } from '@/onboarding';

const props = defineProps({ controller: { type: OnBoarding, required: true } });
const state = props.controller.state;
const config = props.controller.config;
const theme = useThemeVars();
const titleId = useId();
const contentId = useId();
const dialog = ref<HTMLDialogElement | null>(null);
const card = ref<HTMLElement | null>(null);

onMounted(() => {
  if (dialog.value && card.value) props.controller.attach(dialog.value, card.value);
});

// 视图被单独卸载时也必须移除模态和捕获监听；页面重复销毁是安全的。
onBeforeUnmount(() => props.controller.dispose());
</script>

<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      class="onboarding-overlay"
      :aria-labelledby="titleId"
      :aria-describedby="contentId"
      aria-modal="true"
      :style="{ '--onboarding-mask': `rgba(0, 0, 0, ${config.backgroundOpacity ?? 0.6})`, color: theme.textColor1 }"
      @cancel.prevent="controller.skip()"
      @close="!dialog?.open && controller.close()"
    >
      <div
        class="onboarding-highlight"
        aria-hidden="true"
        :class="{ 'onboarding-highlight--missing': !state.rect }"
        :style="state.rect ? { left: `${state.rect.left}px`, top: `${state.rect.top}px`, width: `${state.rect.width}px`, height: `${state.rect.height}px`, borderColor: theme.primaryColor } : {}"
      />
      <section
        ref="card"
        class="onboarding-card flex flex-col gap-4 p-5 rounded-lg"
        :style="{ ...state.cardStyle, background: theme.modalColor, boxShadow: theme.boxShadow3, border: `1px solid ${theme.borderColor}` }"
        :aria-busy="state.busy"
      >
        <div class="text-12px" :style="{ color: theme.textColor3 }" aria-live="polite">
          新手引导 · {{ state.index + 1 }} / {{ state.steps.length }}
        </div>
        <h2 :id="titleId" class="m-0 text-18px font-600" :style="config.titleStyle">
          {{ state.steps[state.index]?.title || '编辑器新手引导' }}
        </h2>
        <p :id="contentId" class="m-0 text-14px leading-6 whitespace-pre-wrap break-words" :style="config.contentStyle">
          {{ state.steps[state.index]?.content }}
        </p>
        <p v-if="state.busy" class="m-0 text-12px" :style="{ color: theme.textColor3 }" role="status">正在准备此步骤…</p>
        <p v-else-if="!state.rect && !state.entryFailed" class="m-0 text-12px" :style="{ color: theme.textColor3 }">
          当前界面未显示此区域，你可以继续查看下一步。
        </p>
        <p v-if="state.error" role="alert" class="m-0 text-12px" :style="{ color: theme.errorColor }">{{ state.error }}</p>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <NButton text size="small" @click="controller.skip()">跳过引导</NButton>
          <div class="flex gap-2">
            <NButton v-if="state.entryFailed" size="small" :disabled="state.busy" @click="controller.retry()">重试</NButton>
            <NButton size="small" :disabled="state.index === 0 || state.busy" @click="controller.prev()">上一步</NButton>
            <NButton autofocus type="primary" size="small" :loading="state.busy" @click="controller.next()">
              {{ state.index === state.steps.length - 1 ? '完成' : '下一步' }}
            </NButton>
          </div>
        </div>
      </section>
    </dialog>
  </Teleport>
</template>

<style scoped>
/* 原生顶层模态隔离背景交互，镂空阴影只负责高亮，不修改目标元素。 */
.onboarding-overlay {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  background: transparent;
}
.onboarding-overlay::backdrop { background: transparent; }
.onboarding-highlight {
  position: absolute;
  box-sizing: border-box;
  border: 2px solid;
  border-radius: 6px;
  box-shadow: 0 0 0 100vmax var(--onboarding-mask);
  pointer-events: none;
}
.onboarding-highlight--missing {
  inset: 0;
  border: 0;
  border-radius: 0;
  background: var(--onboarding-mask);
  box-shadow: none;
}
.onboarding-card {
  position: absolute;
  box-sizing: border-box;
  width: min(360px, calc(100vw - 24px));
  max-height: calc(100dvh - 24px);
  overflow: auto;
}
</style>
